import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RecordsTable = ({ 
  records, 
  loading, 
  onViewRecord, 
  onExportResults,
  pagination,
  onPageChange
}) => {
  const [selectedRecords, setSelectedRecords] = useState([]);

  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prev => {
      if (prev?.includes(recordId)) {
        return prev?.filter(id => id !== recordId);
      }
      return [...prev, recordId];
    });
  };

  const handleSelectAll = () => {
    if (selectedRecords?.length === records?.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records?.map(record => record?.id) || []);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Searching records...</p>
        </div>
      </div>
    );
  }

  if (!records?.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={24} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Records Found</h3>
          <p className="text-muted-foreground mb-4">
            No arrestee records match your search criteria. Try adjusting your search terms.
          </p>
          <Button
            onClick={() => window.location?.reload()}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <Icon name="RefreshCw" size={16} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-foreground">
              Search Results ({records?.length || 0} records)
            </h3>
            {selectedRecords?.length > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                {selectedRecords?.length} selected
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {selectedRecords?.length > 0 && (
              <Button
                onClick={() => onExportResults?.(selectedRecords)}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Icon name="Download" size={16} />
                <span>Export Selected</span>
              </Button>
            )}
            
            <Button
              onClick={() => onExportResults?.(records?.map(r => r?.id))}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <Icon name="FileText" size={16} />
              <span>Export All</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4">
                <input
                  type="checkbox"
                  checked={selectedRecords?.length === records?.length && records?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Name</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">DOB</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Case #</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Booking #</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Charges</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Bond</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Arrest Date</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Upload Date</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records?.map((record, index) => (
              <tr 
                key={record?.id}
                className={`border-b border-border hover:bg-muted/50 ${
                  index % 2 === 0 ? 'bg-background' : 'bg-card'
                }`}
              >
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedRecords?.includes(record?.id)}
                    onChange={() => handleSelectRecord(record?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">
                    {record?.first_name} {record?.last_name}
                  </div>
                  {record?.city && (
                    <div className="text-sm text-muted-foreground">
                      {record?.city}, {record?.state}
                    </div>
                  )}
                </td>
                <td className="p-4 text-sm text-foreground">
                  {formatDate(record?.date_of_birth)}
                </td>
                <td className="p-4 text-sm text-foreground">
                  {record?.case_number || 'N/A'}
                </td>
                <td className="p-4 text-sm text-foreground">
                  {record?.booking_number || 'N/A'}
                </td>
                <td className="p-4 text-sm text-foreground max-w-xs truncate">
                  {record?.charges || 'N/A'}
                </td>
                <td className="p-4 text-sm text-foreground">
                  {formatCurrency(record?.bond_amount)}
                </td>
                <td className="p-4 text-sm text-foreground">
                  {formatDate(record?.arrest_date)}
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {formatDate(record?.created_at)}
                </td>
                <td className="p-4">
                  <Button
                    onClick={() => onViewRecord?.(record)}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <Icon name="Eye" size={14} />
                    <span>View</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination?.totalPages > 1 && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing page {pagination?.page} of {pagination?.totalPages} ({pagination?.total} total records)
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => onPageChange?.(pagination?.page - 1)}
                disabled={pagination?.page <= 1}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Icon name="ChevronLeft" size={16} />
                <span>Previous</span>
              </Button>
              
              <span className="text-sm text-foreground px-3">
                Page {pagination?.page}
              </span>
              
              <Button
                onClick={() => onPageChange?.(pagination?.page + 1)}
                disabled={pagination?.page >= pagination?.totalPages}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordsTable;